/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * ActivityPost / ActivityRow
 * --------------------------
 * One activity in the feed, as a card (ActivityPost) or a dense row
 * (ActivityRow, the "compact" view). Cards are shaped like the social posts
 * they will become — author, time, privacy — but today every one is the
 * viewer's own.
 *
 * The route is fetched and the map mounted only once the reader has actually
 * stopped on the card: half its map area on screen, the page at rest, held
 * there for a second, one download at a time (see RouteGate). Scrolling a
 * long feed past without stopping therefore costs no route requests and no
 * map tiles at all; until then the card shows a quiet map-coloured
 * placeholder. Routes already fetched this session draw straight away.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import FeedIcon from './FeedIcon';
import RouteMap from './RouteMap';
import { useRouteSlot } from './RouteGate';
import { useRoute, useRouteAlreadyLoaded } from '../../pages/feed/useFeedData';
import {
  typeMeta, displayTitle, headlineStats, formatDateTime, hasRoute, yogaInfo, swimLaps,
  /* PRIVACY_LABEL, */ formatDuration,
} from '../../pages/feed/feedModel';

/**
 * True once the element has come within `margin` of the viewport; stays true.
 * No longer what gates route downloads — that is the dwell gate in RouteGate —
 * but kept for anything that just wants "has this scrolled into reach".
 */
export function useSeen(margin = '300px') {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    if (seen || !ref.current) return undefined;
    if (typeof IntersectionObserver === 'undefined') { setSeen(true); return undefined; }
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) { setSeen(true); io.disconnect(); }
    }, { rootMargin: margin });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [seen, margin]);
  return [ref, seen];
}

function Effort({ rpe, compact }) {
  return (
    <span className="fd-effort" aria-label={`Effort ${rpe} of 10`} style={compact ? undefined : { width: '100%' }}>
      {Array.from({ length: 10 }, (_, i) => <i key={i} className={i < rpe ? 'on' : undefined} />)}
    </span>
  );
}

/**
 * What stands in for the map before the route is drawn. Two distinct states,
 * and the difference matters:
 *
 * It shows from the moment a card with a route appears until that route is
 * drawn and its tiles have painted — covering the dwell wait, the queue, the
 * download and Leaflet's own start-up as one continuous state, with no silent
 * gaps in between.
 *
 * This matches the UpVibe app, which makes `pill_loading_route` visible as
 * soon as a card binds without its route (ActivityFeedAdapter: extras == null)
 * rather than only once a download is under way.
 *
 * It makes no network request of its own: no tiles are fetched until the real
 * map mounts underneath it.
 */
function MapWait() {
  return (
    <span className="fd-mapwait">
      <span className="fd-loadpill" role="status">
        <span className="fd-ring" aria-hidden="true" />
        Loading route…
      </span>
    </span>
  );
}

function Media({ activity }) {
  /*
   * Before the dwell gate the route loaded as soon as the card came within
   * 300px of the viewport, so scrolling past 100 cards cost 100 requests:
   *
   * const { route, isLoading, isError } = useRoute(activity.id, seen && hasRoute(activity));
   */
  const wanted = hasRoute(activity);
  // Already fetched (this session, or stored on the device and seeded back by
  // useRouteHydration) → no request and no waiting; it only has to come near
  // the viewport to be drawn.
  const cached = useRouteAlreadyLoaded(wanted ? activity.id : null);
  const [mapRef, dwelled, release] = useRouteSlot(activity.id, wanted && !cached);
  // Mounting a Leaflet map costs tiles and memory even when the route itself
  // is free, so a card far down the page stays a placeholder until it is near
  // the viewport. Without this, reopening the feed with a full route store
  // would mount every visible card's map at once.
  const [seenRef, seen] = useSeen('200px');
  const setMediaRef = useCallback((node) => {
    mapRef.current = node;
    seenRef.current = node;
  }, [mapRef, seenRef]);
  // The wait does not end when the route arrives — Leaflet still has to mount
  // and paint its tiles, which is the longer half on a slow connection. The
  // pill stays up until the map is actually on screen, so there is no silent
  // gap between the spinner going and the map appearing.
  const [mapReady, setMapReady] = useState(false);
  const mayLoad = wanted && (cached || dwelled);
  const { route, isLoading, isError, settled } = useRoute(activity.id, mayLoad);
  // Hand the single download slot to the next card in line.
  useEffect(() => {
    if (dwelled && settled) release();
  }, [dwelled, settled, release]);
  if (activity.type === 'yoga') {
    const y = yogaInfo(activity);
    return (
      <span className="fd-media yoga">
        <span><b>{y.style}</b><span className="s">{[y.intensity, `${Math.round(activity.durationSec / 60)} min`].filter(Boolean).join(' · ')}</span></span>
        <FeedIcon name="self_improvement" />
      </span>
    );
  }
  if (activity.type === 'swim') {
    const laps = swimLaps(activity);
    return (
      <span className="fd-media swim">
        {Array.from({ length: 6 }, (_, i) => <i key={i} />)}
        <span className="lap">
          <b>{laps ? laps.laps : `${Math.round(activity.distanceKm * 1000)}`}</b>
          <span>{laps ? `laps × ${laps.pool} m pool` : 'metres'}</span>
        </span>
      </span>
    );
  }
  if (!wanted || isError || (!isLoading && mayLoad && route.length < 2)) {
    return (
      <span className="fd-media nomap">
        <span><FeedIcon name={typeMeta(activity.type).icon} />No GPS route recorded</span>
      </span>
    );
  }
  const drawn = mayLoad && route.length >= 2 && seen;
  return (
    <span className="fd-media" ref={setMediaRef}>
      {drawn && (
        <RouteMap
          route={route}
          label={`Route of ${displayTitle(activity)}`}
          onReady={() => setMapReady(true)}
        />
      )}
      {(!drawn || !mapReady) && <MapWait />}
      {(activity.elevGain > 0 || activity.kcal > 0) && (
        <span className="fd-over">
          {activity.elevGain > 0 && <span><FeedIcon name="landscape" />+{Math.round(activity.elevGain)} m</span>}
          {activity.kcal > 0 && <span><FeedIcon name="local_fire_department" />{Math.round(activity.kcal).toLocaleString('en-IN')} kcal</span>}
        </span>
      )}
      {drawn && mapReady && <span className="fd-credit">© OpenStreetMap</span>}
    </span>
  );
}

function Stats({ activity }) {
  return (
    <span className="fd-ps">
      {headlineStats(activity).map((s) => (
        <span key={s.label}><small>{s.label}</small><b>{s.value}{s.unit && <em>{s.unit}</em>}</b></span>
      ))}
    </span>
  );
}

export function ActivityPost({ activity, user, best, onOpen }) {
  // The card shell used to carry the observer; the one that matters is now on
  // the map area inside Media, so this no longer needs its own.
  // const [ref] = useSeen();
  const meta = typeMeta(activity.type);
  // Privacy chip hidden until the public feed ships — today every card is the viewer's own.
  // const privacy = PRIVACY_LABEL[activity.privacy];
  return (
    <article className="fd-post">
      <div className="fd-ph">
        <span className="fd-avatar">{user.initials}</span>
        <div style={{ minWidth: 0 }}>
          <b>{user.name}</b>
          <span className="m">{formatDateTime(activity.startedAt)}</span>
        </div>
        {/* <span className="fd-priv" title={privacy}>
          <FeedIcon name={activity.privacy === 'private' ? 'lock' : 'group'} /><span className="pv">{privacy}</span>
        </span> */}
      </div>
      <button type="button" className="fd-open" onClick={() => onOpen(activity)} aria-label={`Open ${displayTitle(activity)}`}>
        <span className="fd-pt"><span className="fd-tico"><FeedIcon name={meta.icon} /></span><h3>{displayTitle(activity)}</h3></span>
        <Stats activity={activity} />
        <Media activity={activity} />
      </button>
      <div className="fd-pf">
        {best
          ? <span className="fd-ach"><FeedIcon name="emoji_events" fill />{best}<em>· last 12 weeks</em></span>
          : activity.rpe
            ? <span className="fd-feel">Felt<Effort rpe={activity.rpe} compact />{activity.rpe}/10</span>
            : <span className="fd-feel">{formatDuration(activity.durationSec)} moving</span>}
        <button type="button" className="fd-link" onClick={() => onOpen(activity)}>View details<FeedIcon name="chevron_right" /></button>
      </div>
    </article>
  );
}

export function ActivityRow({ activity, best, onOpen }) {
  const stats = headlineStats(activity);
  const [first, ...rest] = stats;
  return (
    <button type="button" className="fd-crow" onClick={() => onOpen(activity)}>
      <span className="fd-tico"><FeedIcon name={typeMeta(activity.type).icon} /></span>
      <span style={{ minWidth: 0 }}>
        <b><span>{displayTitle(activity)}</span>{best && <span className="fd-badge"><FeedIcon name="emoji_events" fill />Best</span>}</b>
        <span className="m">{formatDateTime(activity.startedAt)}</span>
      </span>
      <span className="st">
        {first ? `${first.value}${first.unit ? ` ${first.unit}` : ''}` : ''}
        <small>{rest.map((s) => `${s.value}${s.unit}`).join(' · ')}</small>
      </span>
      <FeedIcon name="chevron_right" />
    </button>
  );
}
